import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, IconButton, styled } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { SimplyTestApi } from '../../api/simplytest';
import { SystemBannerItem } from '../../api/types';
import { selectConfig } from '../redux/slice';
import { selectIdToken } from '../../auth/redux/slice';
import { IConfiguration } from '../types';
import { getBannerPalette } from '../utils/bannerColorPalette';

const ui = {
    Banner: styled(Box)({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '9px 20px',
        flexShrink: 0,
    }),
    Icon: styled(CampaignOutlinedIcon)({
        fontSize: '18px',
        flexShrink: 0,
    }),
    Text: styled('div')({
        flex: 1,
        fontSize: '13px',
        fontWeight: 500,
        fontFamily: '"Inter", sans-serif',
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
    }),
};

// Aug 2026 - app-wide announcement banner, shown on every page to every logged-in user whenever a
// Portal Admin has one scheduled and enabled (see Platform Maintenance > System Banner /
// SystemBannerDrawer, and SystemBannersController.GetActive server-side).
//
// Aug 2026 - AllowClose is now a per-banner setting rather than a fixed "never dismissible"
// design: the close button only renders when the server says this particular banner allows it
// (banner.allowClose). Closing calls the server (so it can enforce MaxTimesShown/re-show-next-
// session server-side, not just hide it locally) then clears it from this component's state for
// the rest of the current page session - no need to re-poll GetActive right after closing it.
export const SystemBanner: React.FC<{}> = () => {
    const config: IConfiguration = useSelector(selectConfig)!;
    const idToken = useSelector(selectIdToken);
    const client = useMemo(() => new SimplyTestApi(config.apiBaseUrl, idToken), [config.apiBaseUrl, idToken]);
    const [banner, setBanner] = useState<SystemBannerItem | undefined>();

    useEffect(() => {
        if (!idToken) return;
        let cancelled = false;
        client.getActiveSystemBanner()
            .then(response => {
                if (!cancelled) setBanner(response.status === 200 ? response.data : undefined);
            })
            .catch(() => {
                // No active banner (204) lands here too under some client configs - fail quiet,
                // this is a non-essential announcement, not something worth surfacing an error for.
                if (!cancelled) setBanner(undefined);
            });
        return () => { cancelled = true; };
    }, [client, idToken]);

    if (!banner) return null;

    const palette = getBannerPalette(banner.color);

    const handleClose = () => {
        setBanner(undefined);
        client.closeSystemBanner(banner.systemBannerId).catch(() => {
            // Best-effort - if this fails the banner still just reappears on the next full page
            // load's GetActive call, which is an acceptable fallback for a non-critical UI action.
        });
    };

    return (
        <ui.Banner sx={{ backgroundColor: palette.bg, borderBottom: `1px solid ${palette.border}` }}>
            <ui.Icon sx={{ color: palette.icon }} />
            <ui.Text sx={{ color: palette.text }}>{banner.message}</ui.Text>
            {banner.allowClose && (
                <IconButton size="small" onClick={handleClose} sx={{ flexShrink: 0, padding: '2px', '& svg': { fontSize: '16px', color: palette.icon } }}>
                    <CloseIcon />
                </IconButton>
            )}
        </ui.Banner>
    );
};
